import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { CheckCircle2, FileText, Link2, Loader2, RefreshCw, Trash2, Upload } from 'lucide-react'
import { apiFetch, apiGet, apiPost } from '../../lib/api'
import { Button } from '../ui/button'

interface FileAssociation {
  id: string
  connection_id?: string | null
  conversation_id?: string | null
}

export interface ProjectFileSelection {
  id: string
  file_name: string
}

interface ProjectFile {
  id: string
  source_type: string
  file_name?: string | null
  file_size_bytes?: number | null
  mime_type?: string | null
  chunk_count: number
  processing_status: 'pending' | 'processing' | 'ready' | 'failed'
  processing_error?: string | null
  ingested_at: string
  associations: FileAssociation[]
}

function formatBytes(value?: number | null) {
  if (!value) return 'Size unavailable'
  if (value < 1024) return `${value} B`
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`
  return `${(value / (1024 * 1024)).toFixed(1)} MB`
}

function sourceLabel(value: string) {
  return value.replaceAll('_', ' ').replace(/\b\w/g, character => character.toUpperCase())
}

export default function ProjectFilesPanel({
  projectId, connectionId, conversationId, onSelectionChange,
}: {
  projectId: string
  connectionId?: string | null
  conversationId: string
  onSelectionChange?: (files: ProjectFileSelection[]) => void
}) {
  const [items, setItems] = useState<ProjectFile[]>([])
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [error, setError] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const response = await apiGet(`/api/ingest/projects/${projectId}/files`)
      const nextItems: ProjectFile[] = Array.isArray(response?.items) ? response.items : []
      setItems(nextItems)
      onSelectionChange?.(nextItems.filter(item => item.associations.some(link => link.conversation_id === conversationId)).map(item => ({ id: item.id, file_name: item.file_name || 'Uploaded file' })))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load project files')
    } finally {
      setLoading(false)
    }
  }, [conversationId, onSelectionChange, projectId])

  useEffect(() => { load() }, [load])

  const associatedCount = useMemo(
    () => items.filter(item => item.associations.some(link => link.conversation_id === conversationId)).length,
    [conversationId, items],
  )

  const uploadFile = async (file?: File) => {
    if (!file || uploading) return
    setUploading(true)
    setError('')
    try {
      const body = new FormData()
      body.append('file', file)
      body.append('project_id', projectId)
      body.append('conversation_id', conversationId)
      if (connectionId) body.append('connection_id', connectionId)
      const response = await apiFetch('/api/ingest/file', { method: 'POST', body })
      if (!response.ok) {
        const payload = await response.json().catch(() => null)
        throw new Error(payload?.detail || 'File processing failed')
      }
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'File processing failed')
    } finally {
      setUploading(false)
      if (inputRef.current) inputRef.current.value = ''
    }
  }

  const reuseFile = async (item: ProjectFile) => {
    setBusyId(item.id)
    setError('')
    try {
      await apiPost(`/api/ingest/projects/${projectId}/files/${item.id}/associations`, {
        connection_id: connectionId || null,
        conversation_id: conversationId,
      })
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not associate file')
    } finally {
      setBusyId(null)
    }
  }

  const stopUsingFile = async (item: ProjectFile) => {
    setBusyId(item.id)
    setError('')
    try {
      const response = await apiFetch(`/api/ingest/projects/${projectId}/files/${item.id}/associations/${conversationId}`, { method: 'DELETE' })
      if (!response.ok) throw new Error('Could not remove file selection')
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not remove file selection')
    } finally {
      setBusyId(null)
    }
  }

  const removeFile = async (item: ProjectFile) => {
    if (!window.confirm(`Remove ${item.file_name || 'this file'} from the project library?`)) return
    setBusyId(item.id)
    setError('')
    try {
      const response = await apiFetch(`/api/ingest/sources/${item.id}`, { method: 'DELETE' })
      if (!response.ok) throw new Error('Could not remove file')
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not remove file')
    } finally {
      setBusyId(null)
    }
  }

  return (
    <div className="project-files-panel">
      <div className="project-files-head">
        <div><span className="workspace-kicker">Project knowledge</span><h3>Files</h3><p>Upload once, then reuse processed knowledge across this project's connections and conversations.</p></div>
        <button type="button" onClick={load} aria-label="Refresh project files"><RefreshCw size={13} /></button>
      </div>

      <div className="project-files-actions">
        <input ref={inputRef} type="file" hidden accept=".json,.yaml,.yml,.txt,.md,.pdf,.xml,.wsdl,.csv,.sav" onChange={event => uploadFile(event.target.files?.[0])} />
        <Button size="sm" variant="accent" onClick={() => inputRef.current?.click()} disabled={uploading}>
          {uploading ? <Loader2 size={12} className="spin" /> : <Upload size={12} />} {uploading ? 'Processing…' : 'Upload file'}
        </Button>
        <span>{items.length} project file{items.length === 1 ? '' : 's'} · {associatedCount} used here</span>
      </div>

      <p className="project-files-safety">Supported knowledge files are processed server-side into searchable chunks. File contents are not stored in browser drafts.</p>
      {error && <p className="project-files-error">{error}</p>}

      {loading ? <div className="project-files-empty"><Loader2 size={18} className="spin" /><p>Loading project files…</p></div> : !items.length ? (
        <div className="project-files-empty"><FileText size={20} /><strong>No project files yet</strong><p>Add API specifications, Postman collections, WSDLs, samples, mappings, or SAV files.</p></div>
      ) : (
        <div className="project-files-list">
          {items.map(item => {
            const usedHere = item.associations.some(link => link.conversation_id === conversationId)
            return <article className="project-file-card" key={item.id}>
              <div className={`project-file-icon ${item.processing_status}`}><FileText size={15} /></div>
              <div className="project-file-main">
                <strong title={item.file_name || 'Uploaded file'}>{item.file_name || 'Uploaded file'}</strong>
                <span>{sourceLabel(item.source_type)} · {formatBytes(item.file_size_bytes)}</span>
                <small>{item.processing_status === 'ready' ? `${item.chunk_count || 0} searchable chunks` : item.processing_status}{item.processing_error ? ` · ${item.processing_error}` : ''}</small>
              </div>
              <div className="project-file-controls">
                {usedHere ? <button type="button" className="project-file-used" onClick={() => stopUsingFile(item)} disabled={busyId === item.id}><CheckCircle2 size={11} /> Stop using</button> : <button type="button" onClick={() => reuseFile(item)} disabled={busyId === item.id || item.processing_status !== 'ready'}><Link2 size={11} /> Use here</button>}
                <button type="button" className="danger" aria-label={`Remove ${item.file_name || 'file'}`} onClick={() => removeFile(item)} disabled={busyId === item.id}><Trash2 size={11} /></button>
              </div>
            </article>
          })}
        </div>
      )}
    </div>
  )
}
